import { useEffect, useRef, useState } from 'react';
import { REACTION_CHOICES, groupReactions, myReaction, type AppliedReaction } from '@wa/shared';

interface Props {
  reactions: AppliedReaction[];
  myAgentId: number | null;
  /** Right-aligned for our own messages, left for the customer's. */
  outbound: boolean;
  disabled: boolean;
  onPick: (emoji: string) => void;
  onClear: () => void;
}

/**
 * The reaction chips under a bubble, and the control that adds one.
 *
 * The trigger is visible at all times rather than on hover. A hover-only
 * affordance simply does not exist on a phone, and the alternative — detecting
 * touch — is a branch that gets one of the two cases wrong on somebody's device.
 * One rule, forty percent opacity, full on hover or keyboard focus.
 */
export function ReactionBar({ reactions, myAgentId, outbound, disabled, onPick, onClear }: Props) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  const groups = groupReactions(reactions, myAgentId);
  const mine = myReaction(reactions, myAgentId);

  // Close on an outside click or Escape. Both, because a picker that can only be
  // dismissed by choosing something traps a keyboard user.
  useEffect(() => {
    if (!open) return;

    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function choose(emoji: string) {
    setOpen(false);
    // Tapping the emoji you already have is how WhatsApp removes it.
    if (emoji === mine) onClear();
    else onPick(emoji);
  }

  return (
    <div
      ref={root}
      className={[
        'relative mt-0.5 flex items-center gap-1',
        outbound ? 'flex-row-reverse' : 'flex-row',
      ].join(' ')}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-label={mine ? 'Change your reaction' : 'React to this message'}
        aria-expanded={open}
        className="tap grid size-6 shrink-0 place-items-center rounded-full text-(--color-muted) opacity-40 transition hover:bg-(--color-sunken) hover:opacity-100 focus-visible:opacity-100 disabled:opacity-20"
      >
        <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
          <circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="7.4" cy="8.2" r="1" fill="currentColor" />
          <circle cx="12.6" cy="8.2" r="1" fill="currentColor" />
          <path d="M7 12.1a3.6 3.6 0 0 0 6 0" fill="none" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" />
        </svg>
      </button>

      {/* min-w-0 so a long row of chips shrinks inside the bubble column instead
          of pushing the thread sideways — the same trap the tag chips fell into. */}
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        {groups.map((group) => (
          <button
            key={group.emoji}
            type="button"
            disabled={disabled}
            onClick={() => choose(group.emoji)}
            title={group.who.join(', ')}
            className={[
              'tap flex shrink-0 items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-small leading-none transition',
              group.mine
                ? 'border-(--color-brand) bg-(--color-brand-wash) text-(--color-brand)'
                : 'border-(--color-line) bg-(--color-surface) text-(--color-muted) hover:bg-(--color-sunken)',
            ].join(' ')}
          >
            <span aria-hidden="true">{group.emoji}</span>
            {group.count > 1 && <span className="tabular-nums text-micro">{group.count}</span>}
            <span className="sr-only">
              {group.emoji} from {group.who.join(', ')}
            </span>
          </button>
        ))}
      </div>

      {open && (
        <div
          role="menu"
          className={[
            // w-max is load-bearing. An absolutely positioned box takes its
            // containing block's width, and the containing block here is the bar
            // — which, on a message with no reactions yet, is just the 24px
            // trigger. Without it flex-wrap wrapped after every single emoji and
            // the picker came out as a vertical column down the middle of the
            // thread. max-w keeps it inside the narrowest phone regardless.
            'absolute bottom-full z-20 mb-1 flex w-max max-w-[calc(100vw-2rem)] flex-wrap items-center gap-0.5',
            'rounded-full border border-(--color-line) bg-(--color-surface) px-1.5 py-1 shadow-lg',
            outbound ? 'right-0' : 'left-0',
          ].join(' ')}
        >
          {REACTION_CHOICES.map((emoji) => (
            <button
              key={emoji}
              type="button"
              role="menuitem"
              onClick={() => choose(emoji)}
              aria-label={emoji === mine ? `Remove ${emoji}` : `React with ${emoji}`}
              className={[
                'tap grid size-8 place-items-center rounded-full text-title leading-none transition hover:bg-(--color-sunken)',
                emoji === mine ? 'bg-(--color-brand-wash)' : '',
              ].join(' ')}
            >
              {emoji}
            </button>
          ))}

          {mine && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onClear(); }}
              aria-label="Remove your reaction"
              className="tap grid size-8 place-items-center rounded-full text-(--color-muted) transition hover:bg-(--color-sunken) hover:text-(--color-bad)"
            >
              <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                <path d="M6 6l8 8M14 6l-8 8" fill="none" stroke="currentColor" strokeWidth="1.7"
                      strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
